import * as RadixPopover from "@radix-ui/react-popover";
import { Check } from "@phosphor-icons/react";
import { forwardRef, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding, floatingLayerClassName, searchableSelectContentCollisionClassName, searchableSelectListboxCollisionClassName } from "@/components/ui/overlays/overlayPositioning";
import { Spinner } from "../../feedback/Spinner";

export type SuggestInputItem = {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
};

export type SuggestInputProps = {
  suggestions: SuggestInputItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onSuggestionSelect?: (item: SuggestInputItem) => void;
  renderItem?: (item: SuggestInputItem) => ReactNode;
  placeholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

export const SuggestInput = forwardRef<HTMLInputElement, SuggestInputProps>(
  ({ suggestions, value, defaultValue = "", onValueChange, onSuggestionSelect, renderItem, placeholder = "Search or type", emptyText = "No suggestions found", loading, disabled, error, className, id, "aria-describedby": describedBy, "aria-invalid": ariaInvalid }, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const currentValue = value ?? internalValue;
    const visibleSuggestions = useMemo(() => suggestions, [suggestions]);
    const showPanel = open && (loading || visibleSuggestions.length > 0 || currentValue.length > 0);

    const assignInputRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
    };

    const commitValue = (nextValue: string) => {
      if (value === undefined) setInternalValue(nextValue);
      onValueChange?.(nextValue);
    };

    const selectSuggestion = (item: SuggestInputItem) => {
      if (item.disabled) return;
      commitValue(item.value);
      onSuggestionSelect?.(item);
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setOpen(true);
        setActiveIndex((index) => Math.min(index + 1, Math.max(visibleSuggestions.length - 1, 0)));
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      }
      if (event.key === "Escape") setOpen(false);
      if (event.key === "Enter" && showPanel && visibleSuggestions[activeIndex]) {
        event.preventDefault();
        selectSuggestion(visibleSuggestions[activeIndex]);
      }
    };

    return (
      <RadixPopover.Root open={showPanel} onOpenChange={setOpen}>
        <RadixPopover.Anchor asChild>
          <input
            ref={assignInputRef}
            id={id}
            value={currentValue}
            disabled={disabled}
            aria-autocomplete="list"
            aria-expanded={showPanel}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            placeholder={placeholder}
            className={cn(
              "bfrs-h-9 bfrs-w-full bfrs-rounded-lg bfrs-border bfrs-bg-surface-card bfrs-px-3 bfrs-text-sm bfrs-text-text-primary bfrs-outline-none bfrs-transition placeholder:bfrs-text-text-placeholder disabled:placeholder:bfrs-text-text-muted focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/20 aria-[expanded=true]:bfrs-ring-2 aria-[expanded=true]:bfrs-ring-border-focus/20 disabled:bfrs-cursor-not-allowed disabled:bfrs-bg-surface-muted disabled:bfrs-text-text-disabled",
              error
                ? "bfrs-border-danger focus-visible:bfrs-border-danger focus-visible:bfrs-ring-danger/15 aria-[expanded=true]:bfrs-border-danger aria-[expanded=true]:bfrs-ring-danger/15"
                : "bfrs-border-border-control focus-visible:bfrs-border-border-focus aria-[expanded=true]:bfrs-border-border-focus",
              className
            )}
            onChange={(event) => {
              commitValue(event.currentTarget.value);
              setOpen(true);
              setActiveIndex(0);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
          />
        </RadixPopover.Anchor>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <RadixPopover.Content
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              onOpenAutoFocus={(event) => event.preventDefault()}
              className={cn(floatingLayerClassName, "bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-1 bfrs-shadow-elevated bfrs-outline-none bfrs-animate-content-in", searchableSelectContentCollisionClassName)}
            >
              <div role="listbox" className={searchableSelectListboxCollisionClassName}>
                {loading ? (
                  <div className="bfrs-flex bfrs-h-16 bfrs-items-center bfrs-justify-center bfrs-text-text-muted">
                    <Spinner size="sm" />
                  </div>
                ) : visibleSuggestions.length === 0 ? (
                  <div className="bfrs-px-3 bfrs-py-4 bfrs-text-sm bfrs-text-text-muted">{emptyText}</div>
                ) : (
                  visibleSuggestions.map((item, index) => {
                    const selected = item.value === currentValue;
                    return (
                      <button
                        key={item.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        disabled={item.disabled}
                        className={cn("bfrs-flex bfrs-w-full bfrs-items-center bfrs-gap-2 bfrs-rounded-sm bfrs-px-3 bfrs-py-2 bfrs-text-left bfrs-text-sm bfrs-text-text-primary bfrs-outline-none hover:bfrs-bg-primary-soft disabled:bfrs-text-text-disabled", selected && "bfrs-font-bold", activeIndex === index && "bfrs-bg-primary-soft")}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => selectSuggestion(item)}
                      >
                        <span className="bfrs-min-w-0 bfrs-flex-1">{renderItem ? renderItem(item) : <DefaultSuggestItem item={item} selected={selected} />}</span>
                        {selected && <Check size={14} weight="bold" className="bfrs-text-primary" />}
                      </button>
                    );
                  })
                )}
              </div>
            </RadixPopover.Content>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

function DefaultSuggestItem({ item, selected }: { item: SuggestInputItem; selected: boolean }) {
  return (
    <span className="bfrs-grid bfrs-gap-0.5">
      <span className={cn("bfrs-truncate", selected ? "bfrs-font-bold" : "bfrs-font-medium")}>{item.label}</span>
      {item.description && <span className="bfrs-truncate bfrs-text-xs bfrs-text-text-muted">{item.description}</span>}
    </span>
  );
}

SuggestInput.displayName = "SuggestInput";
